// api.js
const SANITY_PROJECT_ID = "qj2tj4id";
const DATASET = "portfolio";
const API_URL = `https://${SANITY_PROJECT_ID}.api.sanity.io/v2021-10-21/data/query/${DATASET}`;

// Sanity CDN image transform: request only the pixels a slot actually needs,
// let the CDN pick the best format (WebP/AVIF). Width is scaled by the
// device pixel ratio (capped at 2x) so Retina/HiDPI screens - the norm for
// the Mac-heavy audience viewing a design portfolio - don't get a stretched,
// soft image. Video/file URLs pass through.
export const imgUrl = (url, width) => {
  if (!url || !url.includes("cdn.sanity.io/images")) return url;
  const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
  const targetWidth = Math.round(width * dpr);
  return `${url}?w=${targetWidth}&fit=max&auto=format&q=80`;
};

// Full-resolution variant for the lightbox: original pixel dimensions,
// but still re-encoded by the CDN so the download stays reasonable.
export const fullImgUrl = (url) => {
  if (!url || !url.includes("cdn.sanity.io/images")) return url;
  return `${url}?auto=format&q=90`;
};

const runQuery = async (query) => {
  const res = await fetch(`${API_URL}?query=${encodeURIComponent(query)}`);
  if (!res.ok) throw new Error(`Sanity request failed: ${res.status}`);
  const data = await res.json();
  return data.result;
};

const homepageGalleryQuery = `*[_type == "homepageGallery"][0]{
  items[]{
    "_key": _key,
    "_type": _type,
    "url": select(_type == "galleryImageItem" => image.asset->url),
    "videoUrl": select(_type == "galleryVideoItem" => video.asset->url),
    "projectTitle": project->title,
    "projectSlug": project->slug.current
  }
}`;

export const fetchHomepageGallery = async () => {
  try {
    const result = await runQuery(homepageGalleryQuery);
    return result?.items || [];
  } catch (error) {
    console.error("Error fetching homepage gallery:", error);
    return [];
  }
};

// Shown until the Site Settings document is published in the CMS.
const DEFAULT_ABOUT_TEXT =
  "Paris-based freelance creative working across image-making, graphic design, video, and generative AI. Previously Creative Lead at UNVEIL®.";

const siteSettingsQuery = `*[_id == "siteSettings"][0]{ aboutText }`;

export const fetchSiteSettings = async () => {
  try {
    const result = await runQuery(siteSettingsQuery);
    return { aboutText: result?.aboutText || DEFAULT_ABOUT_TEXT };
  } catch (error) {
    console.error("Error fetching site settings:", error);
    return { aboutText: DEFAULT_ABOUT_TEXT };
  }
};

const projectsQuery = `*[_type == "work" && show==true] | order(orderRank){
  _id,
  sortOrder,
  title,
  "slug": slug.current,
  description,
  credits,
  "video1": { "url": video1 },
  "video2": { "url": video2 },
  tags,
  two,
  show,
  galleryVideoControls,
  "thumbnail": {
    "url": thumbnail.asset->url,
    "width": thumbnail.asset->metadata.dimensions.width,
    "height": thumbnail.asset->metadata.dimensions.height
  },
  "gallery": gallery[]{
    "id": _key,
    "_type": _type,

    "url": select(_type == "image" => asset->url),
    "height": select(_type == "image" => asset->metadata.dimensions.height),
    "width": select(_type == "image" => asset->metadata.dimensions.width),
    "displayScale": select(_type == "image" => displayScale),

    "videoUrl": select(_type == "file" => asset->url)
  }
}`;

export const fetchProjects = async () => {
  try {
    return (await runQuery(projectsQuery)) || [];
  } catch (error) {
    console.error("Error fetching projects from Sanity:", error);
    return [];
  }
};

// Picks the image used for a project's card/thumbnail: the CMS thumbnail if
// set, otherwise the first gallery image. Carries width/height so <img> tags
// can reserve their box before the pixels load, instead of collapsing to 0
// height and reflowing the surrounding grid once the image arrives.
export const cardImage = (proj) => {
  if (proj.thumbnail?.url) return proj.thumbnail;
  return proj.gallery?.find((g) => g._type === "image") || {};
};
